import { pointDistance, type Point, type Quad } from '../../../utils/math';

export const MAX_GRID_SEGMENTS = 16;

export interface ProjectionSegment {
  quad: Quad;
  u0: number;
  u1: number;
}

// Max distance a corner line end may have to the outer grid edge to still
// count as untransformed.
const CORNER_LINE_TOLERANCE = 1;

const distanceToLine = (point: Point, [a, b]: [Point, Point]): number => {
  const length = pointDistance(a, b);
  if (length === 0) return pointDistance(point, a);
  const cross =
    (b[0] - a[0]) * (point[1] - a[1]) - (b[1] - a[1]) * (point[0] - a[0]);
  return Math.abs(cross) / length;
};

export const buildProjectionSegments = (
  corners: Point[],
  cornerLines: Point[][] | undefined,
  uStart: number,
  uTotal: number,
  maxSegments = Infinity,
): ProjectionSegment[] => {
  const quad: Quad = [corners[0], corners[1], corners[2], corners[3]];
  const lines = cornerLines ?? [];

  const isTransformed = lines.some(
    ([top, bottom]) =>
      distanceToLine(top, [quad[0], quad[1]]) > CORNER_LINE_TOLERANCE ||
      distanceToLine(bottom, [quad[3], quad[2]]) > CORNER_LINE_TOLERANCE,
  );
  if (!isTransformed || lines.length >= maxSegments) {
    return [{ quad, u0: uStart, u1: uStart + uTotal }];
  }

  const quads = Array.from(
    { length: lines.length + 1 },
    (_, i): Quad => [
      i === 0 ? quad[0] : lines[i - 1][0],
      i === lines.length ? quad[1] : lines[i][0],
      i === lines.length ? quad[2] : lines[i][1],
      i === 0 ? quad[3] : lines[i - 1][1],
    ],
  );

  const segmentLengths = quads.map(
    ([tl, tr, br, bl]) => pointDistance(bl, br) + pointDistance(tl, tr),
  );
  const totalLength = segmentLengths.reduce((a, b) => a + b, 0);
  const segments: ProjectionSegment[] = [];
  let u = uStart;

  for (let i = 0; i < quads.length; i++) {
    const fraction = totalLength > 0 ? segmentLengths[i] / totalLength : 0;
    segments.push({ quad: quads[i], u0: u, u1: u + fraction * uTotal });
    u = segments[i].u1;
  }

  return segments;
};
